import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
import { Icon, IconName } from '../ui/Icon';
import { Heading } from '../typography/Heading';
import { Text } from '../typography/Text';
import { useNavigation } from '../../hooks/useNavigation';

interface SectorCardProps {
  icon: IconName;
  title: string;
  description: string;
  onClick?: () => void;
}

const SectorCard = ({ icon, title, description, onClick }: SectorCardProps) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div
      className="group relative border border-gray-200 bg-white px-8 py-10 rounded-3xl overflow-hidden hover:border-orange-500/50 transition-colors duration-500 cursor-pointer"
      onMouseMove={handleMouseMove}
      onClick={onClick}
    >
      {/* Spotlight Effect */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(251, 165, 32, 0.1),
              transparent 80%
            )
          `,
        }}
      />

      <div className="relative h-full flex flex-col">
        <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 transition-colors group-hover:bg-orange-500 group-hover:text-white">
          <Icon name={icon} className="h-7 w-7" />
        </div>

        <h3 className="mb-3 text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
          {title}
        </h3>

        <p className="mb-6 text-gray-500 leading-relaxed flex-grow">
          {description}
        </p>

        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors mt-auto">
          <span>Explore Sector</span>
          <Icon name="arrow-right" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  );
};

export default function CoreSectors() {
  const { goTo } = useNavigation();

  const handleSectorClick = (sectorId: string) => {
    goTo(`/sectors/${sectorId}`);
  };

  const sectors = [
    {
      id: 'energy',
      icon: 'zap' as IconName,
      title: 'Energy',
      description: 'Leading exporter of solar power solutions and industrial generators for sustainable energy projects.',
    },
    {
      id: 'vehicles-spare-parts',
      icon: 'car' as IconName,
      title: 'Automotive',
      description: 'Trusted supplier of light & heavy vehicles and genuine spare parts for global markets.',
    },
    {
      id: 'furniture-appliances',
      icon: 'home' as IconName,
      title: 'Furniture',
      description: 'Premium indoor and outdoor furnishing solutions for residential and commercial spaces.',
    },
    {
      id: 'camping-material',
      icon: 'mountain' as IconName,
      title: 'Camping',
      description: 'High-quality outdoor gear, tents, and camper vans for adventure and lifestyle needs.',
    },
    {
      id: 'food-items',
      icon: 'shopping-cart' as IconName,
      title: 'FMCG',
      description: 'Premium quality food items, spices, and canned goods sourced from best farms.',
    },
    {
      id: 'kitchen-bakery-equipment',
      icon: 'utensils-crossed' as IconName,
      title: 'Kitchen Equip',
      description: 'Professional grade kitchen and bakery equipment for commercial establishments.',
    },
  ];

  return (
    <section className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="md:flex md:items-end md:justify-between mb-16">
          <div className="max-w-2xl">
            <Heading level={2} className="text-4xl md:text-5xl lg:text-5xl mb-6">
              Diverse <span className="text-orange-500">Sectors</span>
            </Heading>
            <Text color="#6B7280" className="text-lg">
              We operate across multiple key industries, ensuring a comprehensive portfolio of high-quality products.
            </Text>
          </div>

          <button
            onClick={() => goTo('/sectors/energy')}
            className="hidden md:flex items-center gap-2 px-6 py-3 rounded-full border border-gray-200 hover:border-orange-500 hover:text-orange-600 transition-all font-medium"
          >
            View Details
            <Icon name="arrow-right" className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sectors.map((sector, index) => (
            <SectorCard
              key={index}
              {...sector}
              onClick={() => handleSectorClick(sector.id)}
            />
          ))}
        </div>

        <div className="mt-12 md:hidden text-center">
          <button
            onClick={() => goTo('/sectors/energy')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gray-200 hover:border-orange-500 hover:text-orange-600 transition-all font-medium"
          >
            View Full Catalog
            <Icon name="arrow-right" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
